import React, { useEffect, useState, useRef } from "react";
import { useScheduleBetaContext } from "Pages/MyScheduleBeta/Context/ScheduleBetaContext";
import {
  fromISTStart,
  fromISTEnd,
} from "Pages/MyScheduleBeta/utils/dateHelpers";
import LectureCard from "Pages/MyScheduleBeta/Components/LectureCard";
import MoreSessionsCard from "Pages/MyScheduleBeta/Components/MoreSessionsCard";
import {
  groupSimultaneousEvents,
  layoutEvents,
  layoutMobileEvents,
} from "Pages/MyScheduleBeta/utils/layoutHelpers";
import MobileCollapsedLecturesCard from "Pages/MyScheduleBeta/Components/MobileCollapsedLecturesCard";
import useWindowSize from "utilFunctions/CustomHooks/useWindowSize";

export interface WeekTimeGridProps {
  /** Start hour in 24h format (e.g. 7 for 7 AM) */
  startHour?: number;
  /** End hour in 24h format (e.g. 23 for 11 PM) */
  endHour?: number;
  /** Height in pixels for one hour block */
  hourHeight?: number;
  /** Whether to show the red current time indicator */
  showCurrentTime?: boolean;
  /** The current active event type filter value (e.g. "ALL", "LECTURE") */
  activeEventType: string;
  isWeek: boolean;
  /** Ref to the scrollable parent container */
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function toDate(value: Date | string): Date {
  const d = new Date(value);
  d.setHours(0, 0, 0, 0);
  return d;
}

function toKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function buildDateRange(start: Date, end: Date): Date[] {
  const dates: Date[] = [];
  const cur = new Date(start);
  while (cur <= end) {
    dates.push(new Date(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return dates;
}

const WeekTimeGrid: React.FC<WeekTimeGridProps> = ({
  startHour = 7,
  endHour = 23,
  hourHeight = 80,
  showCurrentTime = true,
  activeEventType,
  isWeek,
  scrollContainerRef,
}) => {
  const { fullSchedules, filterState } = useScheduleBetaContext();
  const [currentTime, setCurrentTime] = useState(new Date());
  const currentTimeRef = useRef<HTMLDivElement>(null);
  const { screen } = useWindowSize();
  const isDesktop = ["xxl", "xl", "lg"].includes(screen);

  console.log("filterState", filterState);
  useEffect(() => {
    if (!showCurrentTime) return;
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(interval);
  }, [showCurrentTime]);

  useEffect(() => {
    if (showCurrentTime && currentTimeRef.current) {
      setTimeout(() => {
        const timeIndicator = currentTimeRef.current;
        if (!timeIndicator) return;

        // Use the parent scroll container if provided, otherwise find the nearest scrollable ancestor
        const container =
          scrollContainerRef?.current ||
          // eslint-disable-next-line quotes
          timeIndicator.closest<HTMLElement>('[style*="overflow"]');
        if (!container) return;

        // Calculate the offset of the time indicator relative to the container
        const indicatorRect = timeIndicator.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const indicatorOffsetInContainer =
          indicatorRect.top - containerRect.top + container.scrollTop;
        const containerHeight = container.clientHeight;

        // Scroll so the current time indicator is centered in the container
        container.scrollTo({
          top: indicatorOffsetInContainer - containerHeight / 2,
          behavior: "smooth",
        });
      }, 100);
    }
  }, [showCurrentTime, scrollContainerRef]);

  const startStr = filterState.startDate;
  const endStr = filterState.endDate;
  if (!startStr || !endStr) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        No week date range selected.
      </div>
    );
  }

  const start = toDate(fromISTStart(startStr));
  const end = toDate(fromISTEnd(endStr));
  const dates = buildDateRange(start, end);

  const hours = [];
  for (let i = startHour; i <= endHour; i++) {
    hours.push(i);
  }

  const formatHour = (hour: number) => {
    if (hour === 0) return "12 AM";
    if (hour < 12) return `${hour} AM`;
    if (hour === 12) return "12 PM";
    return `${hour - 12} PM`;
  };

  // const formatExactTime = (date: Date) => {
  //   let h = date.getHours();
  //   const m = String(date.getMinutes()).padStart(2, "0");
  //   const ampm = h >= 12 ? "pm" : "am";
  //   h = h % 12;
  //   if (h === 0) h = 12;
  //   return `${h}:${m}${ampm}`;
  // };

  const currentHour = currentTime.getHours();
  const currentMinute = currentTime.getMinutes();

  const isCurrentTimeInRange =
    (currentHour > startHour ||
      (currentHour === startHour && currentMinute >= 0)) &&
    (currentHour < endHour || (currentHour === endHour && currentMinute === 0));

  const redLineTop =
    (currentHour - startHour + currentMinute / 60) * hourHeight;
  console.log("fullSchedulesfullSchedules", fullSchedules);
  return (
    <div style={styles.container}>
      {/* Column Headers Row */}
      {/* Grid Lines Wrapper */}
      <div style={styles.gridWrapper}>
        {hours.map((hour, index) => (
          <div
            key={hour}
            style={{
              ...styles.row,
              height: index !== hours.length - 1 ? hourHeight : 0,
            }}
          >
            <div style={styles.timeLabelWrapper}>
              <span style={styles.timeLabel}>{formatHour(hour)}</span>
            </div>
            <div style={styles.gridLine} />
          </div>
        ))}

        {/* Current Time Indicator line */}

        {/* Side-by-side Day Columns overlay */}
        <div style={styles.contentContainer}>
          {dates.map((date, index) => {
            const dateKey = toKey(date);
            const dayEvents = (fullSchedules?.[dateKey] || []).filter(
              (event: any) => {
                if (activeEventType === "ALL") return true;
                return event.eventType === activeEventType;
              }
            );
            const laidOutEvents = isDesktop
              ? layoutEvents(groupSimultaneousEvents(dayEvents))
              : layoutMobileEvents(dayEvents);
            const isCurrentDate =
              date.getFullYear() === currentTime.getFullYear() &&
              date.getMonth() === currentTime.getMonth() &&
              date.getDate() === currentTime.getDate();
            return (
              <div
                key={dateKey}
                style={{
                  ...styles.column,
                  borderRight:
                    index !== dates.length - 1 ? "1px solid #f0f0f0" : "none",
                }}
              >
                {showCurrentTime && isCurrentTimeInRange && isCurrentDate && (
                  <div
                    ref={currentTimeRef}
                    style={{ ...styles.currentTimeWrapper, top: redLineTop }}
                  >
                    <div style={styles.currentTimeLineContainer}>
                      <div style={styles.currentTimeDot} />
                      <div style={styles.currentTimeLine} />
                    </div>
                  </div>
                )}
                {laidOutEvents.map((event: any, cardIndex: number) => {
                  if (event.isMobileCollapsed) {
                    return (
                      <MobileCollapsedLecturesCard
                        key={`${event.batchTimeTableId}-${cardIndex}`}
                        event={event}
                        gridStartHour={startHour}
                        hourHeight={hourHeight}
                        left={event._layout?.left || 4}
                        width={event._layout?.width}
                        right={event._layout?.width ? undefined : 4}
                      />
                    );
                  }
                  if (event.isMoreButton) {
                    return (
                      <MoreSessionsCard
                        key={`${event.batchTimeTableId}-${cardIndex}`}
                        event={event}
                        gridStartHour={startHour}
                        hourHeight={hourHeight}
                        left={event._layout?.left || 4}
                        width={event._layout?.width}
                        right={event._layout?.width ? undefined : 4}
                      />
                    );
                  }
                  return (
                    <LectureCard
                      key={`${event.batchTimeTableId}-${cardIndex}`}
                      event={event}
                      gridStartHour={startHour}
                      hourHeight={hourHeight}
                      left={event._layout?.left || 4}
                      width={event._layout?.width}
                      right={event._layout?.width ? undefined : 4}
                      isWeek={isWeek}
                      type={"single"}
                    />
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    width: "100%",
    position: "relative",
    paddingTop: "10px",
    paddingBottom: "20px",
    display: "flex",
    flexDirection: "column",
    overflow: "visible",
    height: "100%",
  },
  headerRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    borderBottom: "1px solid #f0f0f0",
    paddingBottom: "8px",
    marginBottom: "16px",
  },
  timeLabelSpacer: {
    width: "64px",
    flexShrink: 0,
  },
  headersContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "row",
  },
  headerCell: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "2px",
  },
  dayName: {
    fontSize: "12px",
    color: "#757575",
    fontWeight: 500,
    textTransform: "uppercase",
  },
  dateNum: {
    fontSize: "14px",
    color: "#212121",
    fontWeight: 600,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "24px",
    height: "24px",
    textAlign: "center",
    boxSizing: "border-box",
  },
  gridWrapper: {
    position: "relative",
    width: "100%",
    display: "flex",
    flexDirection: "column",
  },
  row: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    width: "100%",
    position: "relative",
  },
  timeLabelWrapper: {
    width: "64px",
    flexShrink: 0,
    display: "flex",
    justifyContent: "flex-end",
    paddingRight: "16px",
    transform: "translateY(-50%)",
  },
  timeLabel: {
    fontSize: "12px",
    color: "#9e9e9e",
    fontWeight: 500,
    textTransform: "uppercase",
  },
  gridLine: {
    flex: 1,
    height: "1px",
    backgroundColor: "#f0f0f0",
  },
  currentTimeWrapper: {
    position: "absolute",
    left: 0,
    right: 0,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    zIndex: 10,
    transform: "translateY(-50%)",
  },
  currentTimeLabelWrapper: {
    width: "64px",
    flexShrink: 0,
    display: "flex",
    justifyContent: "flex-end",
    paddingRight: "12px",
  },
  currentTimeLabel: {
    fontSize: "12px",
    color: "#e53935",
    fontWeight: 600,
  },
  currentTimeLineContainer: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    position: "relative",
  },
  currentTimeDot: {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    backgroundColor: "#e53935",
    position: "absolute",
    left: "-4px",
  },
  currentTimeLine: {
    width: "100%",
    height: "2px",
    backgroundColor: "#e53935",
  },
  contentContainer: {
    position: "absolute",
    top: 0,
    left: "64px",
    right: 0,
    bottom: 0,
    zIndex: 5,
    display: "flex",
    flexDirection: "row",
  },
  column: {
    flex: 1,
    height: "100%",
    position: "relative",
    boxSizing: "border-box",
  },
};

export default WeekTimeGrid;
